<template>
  <div class="question-mastery-trend-mobile">
    <!-- 图表容器 -->
    <div class="chart-card">
      <div class="card-header">
        <span class="card-title">题目掌握趋势</span>
        <div class="card-actions">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExport" />
          </el-tooltip>
          <el-tooltip content="全屏显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
          </el-tooltip>
        </div>
      </div>
      <div class="chart-container" ref="chart"></div>
    </div>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="95%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
    >
      <div slot="title" class="dialog-header">
        <span>题目掌握趋势</span>
        <div class="dialog-operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          </el-tooltip>
          <el-tooltip content="关闭" placement="top">
            <el-button type="text" icon="el-icon-close" @click="closeDialog" />
          </el-tooltip>
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import _ from 'lodash'

export default {
  name: 'QuestionMasteryTrendMobile',
  data() {
    return {
      chart: null,
      fullscreenChart: null,
      chartData: {
        months: [],
        totalQuestions: [],
        acQuestions: [],
        fixedQuestions: [],
        unfixedQuestions: [],
        masteryRates: []
      },
      loading: false,
      dialogVisible: false,
      resizeHandler: null,
      isInitializing: false
    }
  },
  mounted() {
    this.initChart()
    this.fetchData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    this.cleanupFullscreen()
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    dataURLToBlob(dataURL) {
      const arr = dataURL.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) u8arr[n] = bstr.charCodeAt(n)
      return new Blob([u8arr], { type: mime })
    },
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = filename
      link.href = url
      document.body.appendChild(link)
      link.click()
      URL.revokeObjectURL(url)
      document.body.removeChild(link)
    },
    initChart() {
      this.chart = echarts.init(this.$refs.chart)
    },
    resizeChart() {
      this.chart?.resize()
    },
    async fetchData() {
      try {
        this.loading = true
        const res = await request.get('/api/analysis/question/mastery_trend')
        
        if (!res.data) {
          throw new Error('数据格式错误')
        }

        this.chartData = {
          months: res.data.months || [],
          totalQuestions: res.data.total_questions || [],
          acQuestions: res.data.ac_questions || [],
          fixedQuestions: res.data.fixed_questions || [],
          unfixedQuestions: res.data.unfixed_questions || [],
          masteryRates: res.data.mastery_rates || []
        }

        this.updateChart()
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    },
    updateChart() {
      if (!this.chartData.months || this.chartData.months.length === 0) {
        const emptyOption = {
          backgroundColor: '#ffffff',
          title: {
            text: '暂无数据',
            left: 'center',
            top: 'center',
            textStyle: {
              color: '#999',
              fontSize: 14
            }
          }
        }
        this.chart.setOption(emptyOption, true)
        return
      }

      const option = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          confine: true,
          enterable: false,
          hideDelay: 100,
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 11
          },
          formatter: (params) => {
            if (!params || !params.length) return ''
            const title = params[0].axisValue || ''
            let content = ''
            params.forEach(param => {
              if (param.seriesName === '掌握率') {
                content += `<div>${param.seriesName}：${param.value}%</div>`
              } else {
                content += `<div>${param.seriesName}：${param.value}</div>`
              }
            })
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${title}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  ${content}
                </div>
              </div>
            `
          }
        },
        legend: {
          data: ['题目总数', 'AC数量', '已订正数量', '未订正数量', '掌握率'],
          bottom: 0,
          left: 'center',
          itemGap: 8,
          itemWidth: 10,
          itemHeight: 6,
          textStyle: {
            fontSize: 10,
            color: '#666'
          }
        },
        grid: {
          top: '10%',
          left: '5%',
          right: '5%',
          bottom: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.chartData.months,
          axisLabel: {
            fontSize: 10,
            rotate: 45,
            interval: 0
          },
          axisTick: {
            show: false
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '题目数量',
            nameLocation: 'end',
            nameGap: 10,
            nameTextStyle: {
              fontSize: 10,
              color: '#666'
            },
            minInterval: 1,
            axisLabel: {
              fontSize: 10
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
                color: '#E4E7ED'
              }
            },
            axisLine: {
              show: false
            }
          },
          {
            type: 'value',
            name: '掌握率',
            nameLocation: 'end',
            nameGap: 10,
            nameTextStyle: {
              fontSize: 10,
              color: '#666'
            },
            min: 0,
            max: 100,
            interval: 20,
            axisLabel: {
              fontSize: 10,
              formatter: '{value}%'
            },
            splitLine: {
              show: false
            },
            axisLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: '题目总数',
            type: 'bar',
            barMaxWidth: 20,
            itemStyle: {
              color: '#409EFF',
              borderRadius: [2, 2, 0, 0]
            },
            emphasis: {
              focus: 'series'
            },
            data: this.chartData.totalQuestions
          },
          {
            name: 'AC数量',
            type: 'bar',
            stack: 'status',
            barMaxWidth: 20,
            itemStyle: {
              color: '#67C23A',
              borderRadius: [2, 2, 0, 0]
            },
            emphasis: {
              focus: 'series'
            },
            data: this.chartData.acQuestions
          },
          {
            name: '已订正数量',
            type: 'bar',
            stack: 'status',
            barMaxWidth: 20,
            itemStyle: {
              color: '#95DE64',
              borderRadius: [2, 2, 0, 0]
            },
            emphasis: {
              focus: 'series'
            },
            data: this.chartData.fixedQuestions
          },
          {
            name: '未订正数量',
            type: 'bar',
            stack: 'status',
            barMaxWidth: 20,
            itemStyle: {
              color: 'rgba(144, 147, 153, 0.7)',
              borderRadius: [2, 2, 0, 0]
            },
            emphasis: {
              focus: 'series'
            },
            data: this.chartData.unfixedQuestions
          },
          {
            name: '掌握率',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            showSymbol: true,
            symbolSize: 6,
            lineStyle: {
              width: 2,
              color: '#E6A23C'
            },
            itemStyle: {
              color: '#E6A23C'
            },
            emphasis: {
              focus: 'series'
            },
            data: this.chartData.masteryRates
          }
        ]
      }

      this.chart.setOption(option, true)
    },
    handleExport() {
      if (!this.chart) return
      
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.chart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'question_mastery_trend.png')
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    handleExportFullscreen() {
      if (!this.fullscreenChart) return
      
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.fullscreenChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'question_mastery_trend.png')
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    handleFullscreen() {
      this.cleanupFullscreen()
      this.dialogVisible = true
    },
    closeDialog() {
      this.dialogVisible = false
    },
    handleDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.fullscreenChart
          if (!chartDom) return

          this.fullscreenChart = echarts.init(chartDom)
          const option = this.chart.getOption()
          
          // 调整全屏时的布局
          option.grid = {
            top: '10%',
            left: '5%',
            right: '5%',
            bottom: '10%',
            containLabel: true
          }

          this.fullscreenChart.setOption(option)
          
          this.resizeHandler = _.debounce(() => {
            if (this.fullscreenChart) {
              this.fullscreenChart.resize()
            }
          }, 100)

          window.addEventListener('resize', this.resizeHandler)
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleDialogClosed() {
      this.isInitializing = false
    },
    cleanupFullscreen() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }

      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    }
  },
  watch: {
    dialogVisible(val) {
      if (!val && this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    }
  }
}
</script>

<style scoped>
.question-mastery-trend-mobile {
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  margin-bottom: 16px;
}

.chart-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  color: white;
}

.card-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.card-actions .el-button {
  color: #ffffff;
  font-size: 16px;
  padding: 4px;
  margin-left: 8px;
}

.card-actions .el-button:hover {
  color: #409EFF;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.chart-container {
  width: 100%;
  height: 350px;
  padding: 0;
  box-sizing: border-box;
}

/* 全屏弹窗样式 */
.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-operations {
  display: flex;
  gap: 8px;
}

.dialog-operations .el-button {
  color: #ffffff;
  padding: 4px;
}

.dialog-operations .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 500px;
  padding: 20px;
  box-sizing: border-box;
}

/* 移动端优化 */
@media (max-width: 480px) {
  .question-mastery-trend-mobile {
    padding: 0;
    margin-bottom: 12px;
  }
  
  .chart-container {
    height: 300px;
    padding: 0;
  }
  
  .card-header {
    padding: 12px 16px;
  }
  
  .card-title {
    font-size: 14px;
  }
  
  .card-actions .el-button {
    font-size: 14px;
    padding: 2px;
    margin-left: 4px;
  }
  
  .fullscreen-chart {
    height: 400px;
    padding: 16px;
  }
}
</style>
